import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { animalService, CreateAnimalData } from '../services/animalService';
import { uploadService } from '../services/uploadService';
import './AnimalForm.css';

const AnimalForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateAnimalData>({
    name: '',
    species: '',
    breed: '',
    gender: '',
    birth_date: '',
    color: '',
    description: '',
    photo_url: '',
    microchip_number: '',
    responsible_cpf: '',
    responsible_name: '',
    responsible_email: '',
    responsible_phone: '',
    manufacturer: '',
    implant_date: '',
    implant_location: '',
    veterinarian_name: '',
    veterinarian_crmv: '',
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(true);

  useEffect(() => {
    if (id) {
      loadAnimal();
    }
  }, [id]);

  useEffect(() => {
    if (!authLoading && !id && user?.role === 'comum') {
      navigate('/animals', { replace: true });
    }
  }, [authLoading, id, user?.role, navigate]);

  const loadAnimal = async () => {
    try {
      const animal = await animalService.getAnimal(Number(id));
      setCanEdit(animal.can_edit !== false);
      setFormData({
        name: animal.name,
        species: animal.species,
        breed: animal.breed || '',
        gender: animal.gender || '',
        birth_date: animal.birth_date || '',
        color: animal.color || '',
        description: animal.description || '',
        photo_url: animal.photo_url || '',
        microchip_number: animal.microchip?.number || '',
        responsible_cpf: animal.responsible?.cpf || '',
        responsible_name: animal.responsible?.name || '',
        responsible_email: animal.responsible?.email || '',
        responsible_phone: animal.responsible?.phone || '',
        manufacturer: animal.microchip?.manufacturer || '',
        implant_date: animal.microchip?.implant_date || '',
        implant_location: animal.microchip?.implant_location || '',
        veterinarian_name: animal.microchip?.veterinarian_name || '',
        veterinarian_crmv: animal.microchip?.veterinarian_crmv || '',
      });
      setPhotoPreview(animal.photo_url ? uploadService.getImageUrl(animal.photo_url) : null);
    } catch (error) {
      console.error('Erro ao carregar animal:', error);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setFormData((prev) => ({ ...prev, photo_url: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (id && !canEdit) return;
    setLoading(true);

    try {
      let photoUrl: string | null = formData.photo_url?.trim() || null;
      if (photoFile) {
        const uploadResult = await uploadService.uploadImage(photoFile);
        photoUrl = uploadResult.image_url;
      }
      const dataToSend = { ...formData, photo_url: photoUrl ?? undefined };
      if (id && photoUrl === null) (dataToSend as Record<string, unknown>).photo_url = null;
      if (id) {
        await animalService.updateAnimal(Number(id), dataToSend);
      } else {
        await animalService.createAnimal(dataToSend);
      }
      navigate('/animals');
    } catch (error: any) {
      console.error('Erro ao salvar animal:', error);
      alert(error.response?.data?.error || 'Erro ao salvar animal');
    } finally {
      setLoading(false);
    }
  };

  if (!authLoading && !id && user?.role === 'comum') {
    return null;
  }

  const isReadOnly = !!id && !canEdit;

  return (
    <div className="container">
      <h1>{id ? (isReadOnly ? 'Dados do Animal' : 'Editar Animal') : 'Cadastrar Novo Animal'}</h1>

      {isReadOnly && (
        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          Você pode visualizar os dados deste animal. Apenas o dono ou responsável pode editar, informar falecimento e reportar desaparecimento.
        </div>
      )}

      <form onSubmit={handleSubmit} className="card">
        <h2>Informações do Animal</h2>

        <div className="form-group">
          <label>Nome *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            readOnly={isReadOnly}
            disabled={isReadOnly}
          />
        </div>

        <div className="form-group">
          <label>Espécie *</label>
          <select
            name="species"
            value={formData.species}
            onChange={handleChange}
            required
            disabled={isReadOnly}
          >
            <option value="">Selecione</option>
            <option value="Cão">Cão</option>
            <option value="Gato">Gato</option>
            <option value="Outro">Outro</option>
          </select>
        </div>

        <div className="form-group">
          <label>Raça</label>
          <input
            type="text"
            name="breed"
            value={formData.breed}
            onChange={handleChange}
            readOnly={isReadOnly}
            disabled={isReadOnly}
          />
        </div>

        <div className="form-group">
          <label>Sexo</label>
          <select
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            disabled={isReadOnly}
          >
            <option value="">Selecione</option>
            <option value="Macho">Macho</option>
            <option value="Fêmea">Fêmea</option>
          </select>
        </div>

        <div className="form-group">
          <label>Data de Nascimento</label>
          <input
            type="date"
            name="birth_date"
            value={formData.birth_date}
            onChange={handleChange}
            readOnly={isReadOnly}
            disabled={isReadOnly}
          />
        </div>

        <div className="form-group">
          <label>Cor</label>
          <input
            type="text"
            name="color"
            value={formData.color}
            onChange={handleChange}
            readOnly={isReadOnly}
            disabled={isReadOnly}
          />
        </div>

        <div className="form-group">
          <label>Descrição</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            readOnly={isReadOnly}
            disabled={isReadOnly}
          />
        </div>

        <div className="form-group">
          <label>Foto do animal (para carteira de identificação)</label>
          {!isReadOnly && (
          <input
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="form-control"
          />
          )}
          {(photoPreview || formData.photo_url) && (
            <div className="animal-form-photo-preview">
              <img
                src={photoPreview || (formData.photo_url ? uploadService.getImageUrl(formData.photo_url) : '')}
                alt="Foto do animal"
              />
              {!isReadOnly && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleRemovePhoto}>
                  Remover foto
                </button>
              )}
            </div>
          )}
        </div>

        <h2>Responsável pelo animal</h2>
        <p className="form-hint" style={{ marginBottom: '16px', color: '#666', fontSize: '0.9rem' }}>
          Clínicas: informe o CPF do dono do animal. Se o responsável ainda não estiver cadastrado, informe também o nome. Quando o dono acessar a plataforma com esse CPF, o animal será atribuído a ele.
        </p>
        <div className="form-group">
          <label>CPF do responsável</label>
          <input
            type="text"
            name="responsible_cpf"
            value={formData.responsible_cpf || ''}
            onChange={handleChange}
            placeholder="000.000.000-00"
          />
        </div>
        <div className="form-group">
          <label>Nome do responsável (se CPF ainda não cadastrado)</label>
          <input
            type="text"
            name="responsible_name"
            value={formData.responsible_name || ''}
            onChange={handleChange}
            placeholder="Nome completo do dono do animal"
          />
        </div>
        <div className="form-group">
          <label>Email do responsável</label>
          <input
            type="email"
            name="responsible_email"
            value={formData.responsible_email || ''}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label>Telefone do responsável</label>
          <input
            type="text"
            name="responsible_phone"
            value={formData.responsible_phone || ''}
            onChange={handleChange}
            placeholder="(00) 00000-0000"
          />
        </div>

        <h2>Informações do Microchip</h2>

        <div className="form-group">
          <label>Número do Microchip *</label>
          <input
            type="text"
            name="microchip_number"
            value={formData.microchip_number}
            onChange={handleChange}
            required={!id}
            disabled={!!id}
          />
        </div>

        <div className="form-group">
          <label>Fabricante</label>
          <input
            type="text"
            name="manufacturer"
            value={formData.manufacturer}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Data de Implantação</label>
          <input
            type="date"
            name="implant_date"
            value={formData.implant_date}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Local de Implantação</label>
          <input
            type="text"
            name="implant_location"
            value={formData.implant_location}
            onChange={handleChange}
            placeholder="Ex: Pescoço esquerdo"
          />
        </div>

        <div className="form-group">
          <label>Nome do Veterinário</label>
          <input
            type="text"
            name="veterinarian_name"
            value={formData.veterinarian_name}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>CRMV do Veterinário</label>
          <input
            type="text"
            name="veterinarian_crmv"
            value={formData.veterinarian_crmv}
            onChange={handleChange}
          />
        </div>

        <div className="form-actions">
          {!isReadOnly && (
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          )}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/animals')}
          >
            {isReadOnly ? 'Voltar para Buscar Animais' : 'Cancelar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AnimalForm;
